import {
  cloneData, cloneDataOrNull, cloneList, DataModelBuilder, isSameData,
  isSameList, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { Price } from '../price.js';
import { TicketTemplate } from '../ticket-template';
import { CartItemDto } from './cart-item.js';
import { TicketCartItemDto } from './ticket-cart-item.js';
import { TicketSupplementTemplate } from '../ticket-supplement-template.js';
import { VersionRange } from '../../core/version-range.js';

export class TicketCartItemWithSeatingDto extends TicketCartItemDto {
  static readonly TYPE_ID: string = 'f5f5bc259394409a9d5b32c083003428';

  static readonly SCHEMA_VERSION: number = 4;

  public readonly seats: number[];
  private _totalPrice: Price;

  private constructor(
      eventId: string,
      seats: number[],
      ticket: TicketTemplate,
      supplement: TicketSupplementTemplate | null,
      priceModifier: Price | null,
  ) {
    super(eventId, ticket, supplement, priceModifier);

    this.seats = seats;
    this._totalPrice = this.unitPrice.scale(this.seats.length);
  }

  get $typeId(): string {
    return TicketCartItemWithSeatingDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(TicketCartItemWithSeatingDto.SCHEMA_VERSION);
  }

  get quantity(): number {
    return this.seats.length;
  }

  get totalPrice(): Price {
    return this._totalPrice;
  }

  static create(args: {
    eventId: string,
    seats: number[],
    ticket: TicketTemplate,
    supplement: TicketSupplementTemplate | null,
    priceModifier: Price | null
  }) {
    return new TicketCartItemWithSeatingDto(
        args.eventId,
        args.seats,
        args.ticket,
        args.supplement,
        args.priceModifier,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketCartItemWithSeatingDto {
    return new TicketCartItemWithSeatingDto(
        MapUtils.getString(map, 'eventId'),
        MapUtils.getList<number>(map, 'seats', (v) => v as number),
        context.fromData(MapUtils.getRecord(map, 'ticket')),
        context.fromDataOrNull<TicketSupplementTemplate>(MapUtils.getRecordOrNull(map, 'supplement')),
        Price.decodeOrNull(MapUtils.getStringOrNull(map, 'priceModifier')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'eventId': this.eventId,
      'seats': this.seats,
      'ticket': context.toData(this.ticket),
      'supplement': context.toDataOrNull(this.supplement),
      'priceModifier': this.priceModifier?.encode() ?? null,
    };
  }

  clone(): this {
    return new TicketCartItemWithSeatingDto(
        this.eventId,
        cloneList(this.seats),
        cloneData(this.ticket),
        cloneDataOrNull(this.supplement),
        cloneDataOrNull(this.priceModifier),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.eventId === this.eventId &&
      isSameData(other.ticket, this.ticket) &&
      isSameData(other.supplement, this.supplement) &&
      isSameList(other.seats, this.seats) &&
      isSameData(other.priceModifier, this.priceModifier);
  }

  static override assertType(item: CartItemDto): item is TicketCartItemWithSeatingDto {
    return item instanceof TicketCartItemWithSeatingDto;
  }
}

export class TicketCartItemWithSeatingDtoBuilder extends DataModelBuilder<TicketCartItemWithSeatingDto> {
  build(context: SerializationContext, map: SerializedData): TicketCartItemWithSeatingDto {
    return TicketCartItemWithSeatingDto.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TicketCartItemWithSeatingDto.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(TicketCartItemWithSeatingDto.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     * - add `supplement` of type `TicketSupplementTemplate | null`.
     */
    if(version < 2) {
      version = 2;
    }

    /**
     * Version 3:
     * - add `eventId` of type `string | null`.
     */
    if(version < 3) {
      version = 3;
    }

    /**
     * Version 4:
     * - changed `eventId` from `string | null` to `string`.
     *   we don't need to do any migrations, we previously released a version where the event id was non-nullable
     *   in the constructor
     */
    if(version < 4) version = 4;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
